// ═══════════════════════════════════════════════════
// LENIS — Global smooth scroll (CDN)
// ═══════════════════════════════════════════════════
let lenis = null;
if (typeof Lenis !== 'undefined') {
    lenis = new Lenis({ autoRaf: true, duration: 1.1 });
    window.lenis = lenis;
}

// Scroll helper — uses Lenis when available, native smooth otherwise
function smoothScrollTo(target, duration) {
    if (lenis) {
        lenis.scrollTo(target, duration !== undefined ? { duration } : {});
    } else {
        window.scrollTo({ top: target, behavior: 'smooth' });
    }
}

// All in-page "#" links ease back to the top instead of instant-jumping
document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href="#"]');
    if (!link) return;
    e.preventDefault();
    smoothScrollTo(0);
});

// Micro-interactions and effects
document.addEventListener('mousemove', (e) => {
    const cards = document.querySelectorAll('.group');
    cards.forEach(card => {
        const rect = card.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        if (x > 0 && x < rect.width && y > 0 && y < rect.height) {
            card.style.setProperty('--mouse-x', `${x}px`);
            card.style.setProperty('--mouse-y', `${y}px`);
        }
    });
});

// Simple scroll effect for navbar — the `nav-scrolled` class drives all
// bar/dropdown color states via CSS in css/style.css
function updateNavbar() {
    const nav = document.getElementById('navbar');
    if (!nav) return;
    nav.classList.toggle('nav-scrolled', window.scrollY > 50);
}

window.addEventListener('scroll', updateNavbar, { passive: true });
window.addEventListener('load', updateNavbar);
document.addEventListener('DOMContentLoaded', updateNavbar);

// Dashboard scroll perspective animation
document.addEventListener('DOMContentLoaded', () => {
    const dashboardTilt = document.querySelector('.dashboard-tilt');
    if (dashboardTilt) {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    dashboardTilt.classList.add('in-view');
                } else {
                    dashboardTilt.classList.remove('in-view');
                }
            });
        }, { threshold: 0.1 });

        observer.observe(dashboardTilt);
    }
});

// Staggered reveal for trusted-by section
document.addEventListener('DOMContentLoaded', () => {
    const revealObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                entry.target.classList.add('in-view');
                revealObserver.unobserve(entry.target);
            }
        });
    }, { threshold: 0.15 });

    document.querySelectorAll('.reveal').forEach(el => revealObserver.observe(el));
});

// Footer corners: rounded while the footer floats over the page,
// square once the page is scrolled all the way down and it docks.
document.addEventListener('DOMContentLoaded', () => {
    const footer = document.querySelector('.site-footer');
    if (!footer) return;
    const updateFooterCorners = () => {
        const docked = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 150;
        footer.classList.toggle('site-footer--squared', docked);
    };
    window.addEventListener('scroll', updateFooterCorners, { passive: true });
    window.addEventListener('resize', updateFooterCorners);
    updateFooterCorners();
});

// ═══════════════════════════════════════════════════════
// SOLUTIONS TIMELINE — Sui-style center rail
// Cards stay full width, collapsed by default (header +
// footer only). A card's middle visual expands (grid row
// 0fr → 1fr, opening downward) as soon as the collapsed
// card's TOP reaches the middle of the screen (50vh) and
// contracts only when it drops back out through the bottom
// (top ≥ 85vh) — never in the upper viewport.
// The rail tails grow from the square on the side opposite
// the scroll direction, their length following scroll density.
// Desktop only — mobile and reduced-motion fall back to a
// static layout with all cards expanded.
// ═══════════════════════════════════════════════════════
document.addEventListener('DOMContentLoaded', () => {
    const tlMain = document.querySelector('.tl-main');
    if (!tlMain) return;

    const cards = Array.from(document.querySelectorAll('.tl-card'));
    const marker = document.querySelector('.tl-marker');
    if (cards.length === 0 || !marker) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const isMobile = () => window.innerWidth <= 768;

    let ticking = false;
    let lastY = window.scrollY;
    let lastTime = performance.now();
    let tailTop = 0;
    let tailBottom = 0;
    let tailV = 0;            // signed, smoothed velocity — drives cube drift
    const MAX_TAIL = 140;     // px — stays inside the 300px marker band
    const TAIL_K = 100;       // tail px per (px/ms) of scroll density
    const TAIL_SMOOTH = 0.3;  // exponential smoothing per frame (grow + decay)
    const MAX_SHIFT = 16;     // px — max cube drift in the scroll direction
    const SHIFT_K = 10;       // drift px per (px/ms) of scroll density

    // Measure the fully-expanded card height (transitions disabled) and
    // set the inter-card gap to 280% of it — generous blank space between
    // cards in their expanded view. Re-measured on resize / font load.
    function measureGap() {
        tlMain.classList.add('tl-measuring');
        cards.forEach((card) => card.classList.add('tl-card-expanded'));
        let maxH = 0;
        cards.forEach((card) => {
            maxH = Math.max(maxH, card.getBoundingClientRect().height);
        });
        cards.forEach((card) => card.classList.remove('tl-card-expanded'));
        tlMain.classList.remove('tl-measuring');
        // Inter-card gap = 224% of the expanded card height (280% minus 20%).
        const gap = maxH * 2.24;
        tlMain.style.setProperty('--tl-gap', `${gap.toFixed(1)}px`);
        // Right column starts (cardHeight + gap) / 2 below the left one so
        // consecutive (alternating) cards end up uniformly spaced: within a
        // column the step is H + gap, and the interleaved cross-column step
        // is exactly half of that, offset in between.
        tlMain.style.setProperty('--tl-col-offset', `calc(42vh + ${((maxH + gap) / 2).toFixed(1)}px)`);
    }

    function update() {
        if (isMobile()) return;

        const vh = window.innerHeight;

        // Scroll density (velocity) drives the rail tails: scrolling down
        // grows a tail UPWARD from the square (the side opposite the travel
        // direction), scrolling up grows it downward. Tail length follows
        // density; it decays back to zero when scrolling stops.
        const now = performance.now();
        const dt = Math.max(now - lastTime, 1);
        lastTime = now;
        const velocity = (window.scrollY - lastY) / dt; // px per ms
        lastY = window.scrollY;

        const target = Math.min(Math.abs(velocity) * TAIL_K, MAX_TAIL);
        if (velocity >= 0) {
            tailTop += (target - tailTop) * TAIL_SMOOTH;
            tailBottom *= (1 - TAIL_SMOOTH);
        } else {
            tailBottom += (target - tailBottom) * TAIL_SMOOTH;
            tailTop *= (1 - TAIL_SMOOTH);
        }
        marker.style.setProperty('--tl-tail-top', `${tailTop.toFixed(1)}px`);
        marker.style.setProperty('--tl-tail-bottom', `${tailBottom.toFixed(1)}px`);

        // Cube drifts a few px toward the scroll direction — same smoothed
        // velocity, signed: positive while scrolling down, negative up.
        tailV += (velocity - tailV) * TAIL_SMOOTH;
        const shift = Math.max(-MAX_SHIFT, Math.min(MAX_SHIFT, tailV * SHIFT_K));
        marker.style.setProperty('--tl-cube-shift', `${shift.toFixed(1)}px`);

        // Per-card expand/collapse: a card expands the moment its TOP
        // touches the middle of the screen (50vh) — the visual opens
        // downward from the header. It collapses only when its top
        // drops back below 85vh (scrolling back out through the
        // bottom). The 35vh hysteresis band keeps the toggle stable —
        // nothing collapses in the upper viewport.
        const expandY = vh * 0.5;
        const collapseY = vh * 0.85;

        cards.forEach((card) => {
            const c = card.getBoundingClientRect();

            if (c.top <= expandY) {
                card.classList.add('tl-card-expanded');
            } else if (c.top >= collapseY) {
                card.classList.remove('tl-card-expanded');
            }
        });

        ticking = false;
    }

    function teardown() {
        cards.forEach((card) => card.classList.remove('tl-card-expanded'));
        marker.style.setProperty('--tl-tail-top', '0px');
        marker.style.setProperty('--tl-tail-bottom', '0px');
        marker.style.setProperty('--tl-cube-shift', '0px');
    }

    function onScroll() {
        if (!ticking) {
            ticking = true;
            requestAnimationFrame(update);
        }
    }

    function onResize() {
        if (isMobile()) {
            teardown();
            return;
        }
        measureGap();
        update();
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);

    // Re-measure once fonts load (text-driven card heights)
    if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
            measureGap();
            update();
        });
    }

    measureGap();
    update();
});
